/**
 * Picks the quality tier while the game runs: it starts at a tier and steps down while frames drawn
 * at full rate come too slowly, as on a phone that can't keep up. Ambient frames (the loop's 30 fps)
 * don't count. It never steps up by itself; the Tile Lab shows what the higher tiers look like.
 */
import type { Quality } from './look';

/** Full-rate frames slower than this on average (ms) count against the tier: under about 45 fps. */
const SLOW_MS = 22;
/** Frames to average before judging, and the wait after a step. */
const SAMPLES = 90;
const QUIET_MS = 4000;
/** A gap longer than this is a hiccup (a hidden page, a pause), not a measure. */
const HICCUP_MS = 100;
const LOWER: Readonly<Record<Quality, Quality | null>> = {
  high: 'medium',
  medium: 'low',
  low: null,
};

export interface QualityGovernor {
  readonly tier: Quality;
  /** A frame at `now` (ms), `full` when it ran at full rate. Returns the new tier when it stepped down. */
  frame(now: number, full: boolean): Quality | null;
}

/**
 * The tier the game starts at: medium, unless VITE_QUALITY says another (the end-to-end tests start
 * low: headless browsers render in software, and a bigger planet costs them dearly).
 */
export const START_TIER: Quality =
  (['low', 'medium', 'high'] as const).find((q) => q === import.meta.env.VITE_QUALITY) ?? 'medium';

export function createQualityGovernor(start: Quality): QualityGovernor {
  let tier = start;
  /** The previous full-rate frame's time, or -1. */
  let last = -1;
  let average = 0;
  let samples = 0;
  let quietUntil = 0;
  return {
    get tier() {
      return tier;
    },
    frame(now, full) {
      const previous = last;
      last = full ? now : -1;
      if (!full || previous < 0 || now - previous > HICCUP_MS) return null;
      average = samples === 0 ? now - previous : average + (now - previous - average) * 0.05;
      samples++;
      if (now < quietUntil || samples < SAMPLES || average <= SLOW_MS) return null;
      samples = 0;
      const lower = LOWER[tier];
      if (!lower) return null;
      tier = lower;
      quietUntil = now + QUIET_MS;
      return tier;
    },
  };
}
